import { graphql, useStaticQuery } from 'gatsby';
import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { ArticlePreview as ArticlePreviewType } from '../../../types/articlePreview';
import { Background } from '../../../types/background';
import { SanityArticleTag } from '../../../types/common';
import ArticlePreview from '../../ArticlePreview';
import { InternalLinkIcon } from '../../Icons';
import InternalLink from '../../InternalLink';
import './styles.scss';

type ExtraNewsData = {
  background: Background;
  title?: string | null;
  description?: string | null;
  cta?: string | null;
  page: any;
  highlight?: ArticlePreviewType[] | null;
  articles: {
    typeList: 'all' | 'select' | 'tag' | 'category';
    categoryArticles?: {
      _id: string;
      name: string;
    } | null;
    selectArticles?: ArticlePreviewType[] | null;
    tagArticles?: SanityArticleTag[] | null;
  }
}

type ExtraNewsProps = {
  data: ExtraNewsData;
}

type QueryData = {
  allSanityArticles: {
    nodes: ArticlePreviewType[]
  };
}

function getArticlesList(data: ExtraNewsData, allArticles: ArticlePreviewType[]): ArticlePreviewType[] {
  const highlight = data.highlight?.map(article => ({ ...article, highlight: true }));

  if (data.articles.typeList === 'all') {
    const withoutDuplicates = highlight && allArticles.filter(article => {
      return !highlight.some(h => h._id === article._id);
    });
    return highlight ? [...highlight, ...withoutDuplicates] : allArticles;
  }

  if (data.articles.typeList === 'select' && data.articles.selectArticles) {
    return highlight ? [...highlight, ...data.articles.selectArticles] : data.articles.selectArticles;
  }

  if (data.articles.typeList === 'category' && data.articles.categoryArticles?._id) {
    const categoryArticles = allArticles.filter(item => item.categories?.some(cat => cat._id === data.articles.categoryArticles?._id));

    const articlesFormatted = categoryArticles.map(article => ({ ...article, highlight: false }))
    const withoutDuplicates = highlight && articlesFormatted.filter(article => {
      return !highlight.some(h => h._id === article._id);
    });

    return highlight ? [...highlight, ...withoutDuplicates] : articlesFormatted;
  }

  if (data.articles.typeList === 'tag' && data.articles.tagArticles) {
    const tagsIds = data.articles.tagArticles.map(tag => tag._id);

    const filteredArticles = allArticles.filter(article => {
      const articleTags = article.tags?.map(tag => tag._id);
      return articleTags ? articleTags.some(item => tagsIds.includes(item)) : [];
    });

    const withoutDuplicates = highlight && filteredArticles.filter(article => {
      return !highlight.some(h => h._id === article._id);
    });

    return highlight ? [...highlight, ...withoutDuplicates] : filteredArticles;
  }

  return [];
}

const ExtraNews: FunctionComponent<ExtraNewsProps> = ({ data }) => {
  const query = (useStaticQuery<QueryData>(graphql`
    {
      allSanityArticles(sort: {_createdAt: DESC}) {
        ...ArticlesPreviewList
      }
    }
  `));

  const articles: ArticlePreviewType[] = useMemo(() => (
    getArticlesList(data, query.allSanityArticles.nodes)
  ), [data, query, getArticlesList]);

  const listSize = 8;
  const [list, setList] = useState([...articles.slice(0, listSize)]);
  const [loadMore, setLoadMore] = useState(false);
  const [hasMore, setHasMore] = useState(articles.length > listSize);

  function handleLoadMore() {
    setLoadMore(true);
  }

  useEffect(() => {
    if (hasMore && loadMore) {
      const currentLength = list.length;
      const nextResults = currentLength < articles.length ? articles.slice(currentLength, currentLength + listSize) : [];
      setList(prevList => [...prevList, ...nextResults]);
      setLoadMore(false);
    }
  }, [hasMore, loadMore]);

  useEffect(() => {
    const isMore = list.length < articles.length;
    setHasMore(isMore);
  }, [list]);

  return (
    <section className={`extra-news ${data.background}`}>
      <div>
        <div className='extra-news-header'>
          <div>
            <h2>{data.title ? data.title : 'News'}</h2>
            {data.description && (
              <p>{data.description}</p>
            )}
          </div>
          <InternalLink reference={data.page}>
            <span>
              {data.cta ? data.cta : 'Discover more news'}
            </span>
            <InternalLinkIcon />
          </InternalLink>
        </div>

        <div className='extra-news-container'>
          {list.map(article => (
            <ArticlePreview key={article._id} article={article} />
          ))}

          {hasMore && (
            <button
              type='button'
              onClick={handleLoadMore}
              className='primary-button'
            >
              Load more
              <InternalLinkIcon />
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

export default ExtraNews;
